import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Link, Outlet } from 'react-router-dom';
import { scheduleTokenRefresh } from './utils/tokenRefresh';
import Login from './components/Login';
import ResetPasswordForm from './components/ResetPasswordForm';
import AdminDashboard from './components/AdminDashboard';
import ChangePasswordModal from './components/ChangePasswordModal';
import RenalForm from './components/RenalForm';
import AbdomenForm from './components/AbdomenForm';
import ScrotaForm from './components/ScrotaForm';
import LegArterialForm from './components/LegArterialForm';
import UpperExtremityArterialForm from './components/UpperExtremityArterialForm';
import DialysisAccessForm from './components/DialysisAccessForm';
import DVTForm from './components/DVTForm';
import UpperExtremityDVTForm from './components/UpperExtremityDVTForm';
import ThyroidForm from './components/ThyroidForm';
import PelvicForm from './components/PelvicForm';
import FollicleCountForm from './components/FollicleCountForm';
import AortaForm from './components/AortaForm';
import CarotidForm from './components/CarotidForm';
import PediatricAbdomenForm from './components/PediatricAbdomenForm';
import PediatricBrainSpineForm from './components/PediatricBrainSpineForm';
import PediatricHipsForm from './components/PediatricHipsForm';
import SoftTissueForm from './components/SoftTissueForm';
import RenalArteryDopplerForm from './components/RenalArteryDopplerForm';
import FirstTrimesterOBForm from './components/FirstTrimesterOBForm';
import SecondTrimesterMultipleOBForm from './components/SecondTrimesterMultipleOBForm';
import BPPForm from './components/BPPForm';
import LungScreeningForm from './components/LungScreeningForm';
import LungScreeningSelection from './components/LungScreeningSelection';
import { logApiConfig, testApiConnection } from './utils/apiDebug';
import './index.css';
import UserProfile from './components/UserProfile';
import BugReportModal from './components/BugReportModal';

const API_URL = process.env.REACT_APP_API_URL || '';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [token, setToken] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showBugReportModal, setShowBugReportModal] = useState(false);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [facilityInfo, setFacilityInfo] = useState(
    () => { try { return JSON.parse(localStorage.getItem('facilityInfo') || 'null'); } catch { return null; } }
  );
  const [professionRole, setProfessionRole] = useState(
    () => localStorage.getItem('professionRole') || 'sonographer'
  );

  // Holds the cancel function returned by scheduleTokenRefresh
  const cancelRefreshRef = useRef(null);

  // Debug: Log API configuration on app load
  useEffect(() => {
    logApiConfig();

    // Test API connection
    testApiConnection().then(result => {
      if (result.success) {
        console.log('✓ API connection verified');
      } else {
        console.warn('✗ API connection test failed - check if backend is running');
      }
    });
  }, []);

  useEffect(() => {
    // Restore session from localStorage on page load
    const savedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('userInfo');

    if (savedToken && storedUser) {
      const user = JSON.parse(storedUser);
      setToken(savedToken);
      setUserInfo(user);
      setIsLoggedIn(true);
      startRefreshScheduler(savedToken);

      if (user.forcePasswordChange) {
        setShowPasswordModal(true);
      }
    }
    // Cleanup on unmount
    return () => {
      if (cancelRefreshRef.current) cancelRefreshRef.current();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply dark/light class to body whenever theme changes
  useEffect(() => {
    if (theme === 'dark') {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [theme]);

  // Listen for theme changes dispatched by UserProfile after saving settings
  useEffect(() => {
    const handler = () => setTheme(localStorage.getItem('theme') || 'light');
    window.addEventListener('theme-change', handler);
    return () => window.removeEventListener('theme-change', handler);
  }, []);

  // Listen for default-view changes dispatched by UserProfile after saving settings
  useEffect(() => {
    const handler = () => setProfessionRole(localStorage.getItem('professionRole') || 'sonographer');
    window.addEventListener('profession-role-change', handler);
    return () => window.removeEventListener('profession-role-change', handler);
  }, []);

  // Re-fetch facility info (report format, name, logo) on every page load so an admin's
  // change to the facility's report format applies without waiting for a fresh login.
  // The cached copy in localStorage is used until this returns; failures are non-fatal.
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.profile.facilityInfo) {
          setFacilityInfo(data.profile.facilityInfo);
          localStorage.setItem('facilityInfo', JSON.stringify(data.profile.facilityInfo));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start the silent refresh scheduler for a given access token
  const startRefreshScheduler = (accessToken) => {
    if (cancelRefreshRef.current) cancelRefreshRef.current();
    cancelRefreshRef.current = scheduleTokenRefresh(
      accessToken,
      API_URL,
      (newToken) => {
        setToken(newToken);
      },
      () => {
        // Refresh token expired — log the user out with a message
        handleLogout();
        setSessionExpiredMsg('Your session has expired. Please log in again.');
      }
    );
  };

  const handleLogin = (authToken, user, refreshToken) => {
    localStorage.setItem('token', authToken);
    localStorage.setItem('userInfo', JSON.stringify(user));
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    setToken(authToken);
    setUserInfo(user);
    setIsLoggedIn(true);
    setSessionExpiredMsg(null);
    startRefreshScheduler(authToken);

    // Fetch and cache facility info for client-side PDF generation (no PHI)
    fetch(`${API_URL}/api/profile`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.profile.facilityInfo) {
          setFacilityInfo(data.profile.facilityInfo);
          localStorage.setItem('facilityInfo', JSON.stringify(data.profile.facilityInfo));
        }
        if (data.success && data.profile.settings?.profession_role) {
          setProfessionRole(data.profile.settings.profession_role);
          localStorage.setItem('professionRole', data.profile.settings.profession_role);
        }
      })
      .catch(() => {}); // non-fatal — falls back to default name in generators

    if (user.forcePasswordChange) {
      setShowPasswordModal(true);
    }
  };

  const handleLogout = () => {
    if (cancelRefreshRef.current) {
      cancelRefreshRef.current();
      cancelRefreshRef.current = null;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('facilityInfo');
    localStorage.removeItem('professionRole');
    setToken('');
    setUserInfo(null);
    setFacilityInfo(null);
    setProfessionRole('sonographer');
    setIsLoggedIn(false);
  };

  const handlePasswordChangeSuccess = () => {
    // Update user info to reflect password was changed
    const updatedUser = { ...userInfo, forcePasswordChange: false };
    localStorage.setItem('userInfo', JSON.stringify(updatedUser));
    setUserInfo(updatedUser);
    setShowPasswordModal(false);
  };

  // Renders the forced password change modal inside Router context so it can navigate after success
  const ForcedPasswordChange = () => {
    const navigate = useNavigate();
    if (!showPasswordModal) return null;
    return (
      <ChangePasswordModal
        isForced={true}
        onClose={() => {}}
        onSuccess={() => {
          handlePasswordChangeSuccess();
          navigate('/');
        }}
      />
    );
  };

  // Wrapper for form routes: injects token, facilityInfo, and navigate-back into each form
  const FormRoute = ({ Component }) => {
    const navigate = useNavigate();
    return <Component token={token} facilityInfo={facilityInfo} onBack={() => navigate('/')} />;
  };

  // Wrapper for profile route: uses navigate instead of window.location
  const ProfileRoute = () => {
    const navigate = useNavigate();
    return <UserProfile onBack={() => navigate('/')} />;
  };

  // Worksheet Selection Screen
  const WorksheetSelection = () => {
    const navigate = useNavigate();
    return (
      <div className="worksheet-selection">
        <h2>Select Exam Worksheet</h2>

        <p className="landing-view-switch">
          Doing a lung cancer screening intake?{' '}
          <button type="button" className="link-button" onClick={() => navigate('/nurse')}>
            Go to Nurse Intake
          </button>
        </p>

        {/* General Ultrasound Section */}
        <div className="worksheet-section">
          <h3 className="section-title">General Ultrasound</h3>
          <div className="worksheet-cards">
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/renal')}>
              <div className="worksheet-icon">
                <img src="/images/kidney_icon.png" alt="Renal" />
              </div>
              <h3>Renal Ultrasound</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/abdomen')}>
              <div className="worksheet-icon">
                <img src="/images/abdomen.png" alt="Abdomen" />
              </div>
              <h3>Abdomen Ultrasound</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/scrotal')}>
              <div className="worksheet-icon">
                <img src="/images/scrotum.png" alt="Scrotal" />
              </div>
              <h3>Scrotal Ultrasound</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/thyroid')}>
              <div className="worksheet-icon">
                <img src="/images/thyroid.png" alt="Thyroid" />
              </div>
              <h3>Thyroid Ultrasound</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/pelvic')}>
              <div className="worksheet-icon">
                <img src="/images/pelvis.png" alt="Pelvic" />
              </div>
              <h3>Pelvic Ultrasound</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/follicle-count')}>
              <div className="worksheet-icon">
                <img src="/images/pelvis.png" alt="Follicle Count" />
              </div>
              <h3>Follicle Count</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/soft-tissue')}>
              <div className="worksheet-icon">
                <img src="/images/hernia.png" alt="Soft Tissue" />
              </div>
              <h3>Soft Tissue</h3>
            </div>
          </div>
        </div>

        {/* Vascular Ultrasound Section */}
        <div className="worksheet-section">
          <h3 className="section-title">Vascular Ultrasound</h3>
          <div className="worksheet-cards">
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/aorta')}>
              <div className="worksheet-icon">
                <img src="/images/aorta.png" alt="Aorta" />
              </div>
              <h3>Aorta Ultrasound</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/carotid')}>
              <div className="worksheet-icon">
                <img src="/images/carotid.png" alt="Carotid" />
              </div>
              <h3>Carotid Doppler</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/renal-artery-doppler')}>
              <div className="worksheet-icon">
                <img src="/images/kidney_icon.png" alt="Renal Artery Doppler" />
              </div>
              <h3>Renal Artery Doppler</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/dvt')}>
              <div className="worksheet-icon">
                <img src="/images/veins.png" alt="DVT" />
              </div>
              <h3>Lower Extremity DVT</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/upper-extremity-dvt')}>
              <div className="worksheet-icon">
                <img src="/images/arm_vein.png" alt="Upper Extremity DVT" />
              </div>
              <h3>Upper Extremity DVT</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/lower-extremity-arterial')}>
              <div className="worksheet-icon">
                <img src="/images/leg_arterial.jpg" alt="Lower Extremity Arterial" />
              </div>
              <h3>Lower Extremity Arterial</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/upper-extremity-arterial')}>
              <div className="worksheet-icon">
                <img src="/images/arm_arterial.jpg" alt="Upper Extremity Arterial" />
              </div>
              <h3>Upper Extremity Arterial</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/dialysis-access')}>
              <div className="worksheet-icon">
                <img src="/images/arm_vein.png" alt="Dialysis Access" />
              </div>
              <h3>Dialysis Access (Fistula/Graft)</h3>
            </div>
          </div>
        </div>

        {/* Pediatric Ultrasound Section */}
        <div className="worksheet-section">
          <h3 className="section-title">Pediatric Ultrasound</h3>
          <div className="worksheet-cards">
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/pediatric-abdomen')}>
              <div className="worksheet-icon">
                <img src="/images/pediatric.png" alt="Pediatric Abdomen" />
              </div>
              <h3>Pediatric Abdomen</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/pediatric-brain-spine')}>
              <div className="worksheet-icon">
                <img src="/images/pediatric.png" alt="Pediatric Brain and Spine" />
              </div>
              <h3>Pediatric Brain & Spine</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/pediatric-hips')}>
              <div className="worksheet-icon">
                <img src="/images/pediatric.png" alt="Pediatric Hips" />
              </div>
              <h3>Pediatric Hips</h3>
            </div>
          </div>
        </div>

        {/* Obstetric Ultrasound Section */}
        <div className="worksheet-section">
          <h3 className="section-title">Obstetric Ultrasound</h3>
          <div className="worksheet-cards">
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/first-trimester-ob')}>
              <div className="worksheet-icon">
                <img src="/images/ob_us.png" alt="First Trimester OB" />
              </div>
              <h3>First Trimester OB</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/second-trimester-ob')}>
              <div className="worksheet-icon">
                <img src="/images/ob_us.png" alt="Second & Third Trimester OB" />
              </div>
              <h3>Second & Third Trimester OB</h3>
            </div>
            <div role="button" tabIndex={0} className="worksheet-card" onClick={() => navigate('/forms/bpp')}>
              <div className="worksheet-icon">
                <img src="/images/ob_us.png" alt="BPP" />
              </div>
              <h3>BPP</h3>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Layout component: renders the app header + an Outlet for child routes
  const WorksheetLayout = () => (
    <div>
      <header className="app-header">
        <h1>Ultrasound Worksheet System</h1>
        <div className="header-actions">
          <span>Welcome, {userInfo?.fullName || userInfo?.username}</span>
          <button onClick={() => setShowBugReportModal(true)} className="admin-link bug-report-link">
            Report a Bug
          </button>
          <Link to="/profile" className="admin-link">My Profile</Link>
          {userInfo?.role === 'admin' && (
            <Link to="/admin" className="admin-link">Admin Dashboard</Link>
          )}
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>
      <Outlet />
      {showBugReportModal && <BugReportModal onClose={() => setShowBugReportModal(false)} />}
    </div>
  );

  if (!isLoggedIn) {
    // Reset-password links are emailed and must work while logged out. No
    // <Router> is mounted at this point, so this checks window.location
    // directly rather than using react-router hooks.
    if (window.location.pathname === '/reset-password') {
      return <ResetPasswordForm />;
    }
    return <Login onLogin={handleLogin} sessionExpiredMsg={sessionExpiredMsg} />;
  }

  return (
    <div className="App">
      <Router>
        <ForcedPasswordChange />

        <Routes>
          {/* Standalone pages (no app header) */}
          <Route path="/profile" element={<ProfileRoute />} />
          {userInfo?.role === 'admin' && (
            <Route path="/admin" element={<AdminDashboard onLogout={handleLogout} />} />
          )}

          {/* Worksheet pages — layout route provides the app header */}
          <Route element={<WorksheetLayout />}>
            {/* Role-based landing: nurses see the Lung Screening intake card by
                default, sonographers see the full worksheet grid. /worksheets
                and /nurse are always-available stable links so either role can
                switch views regardless of their default. */}
            <Route index element={professionRole === 'nurse' ? <LungScreeningSelection /> : <WorksheetSelection />} />
            <Route path="worksheets" element={<WorksheetSelection />} />
            <Route path="nurse" element={<LungScreeningSelection />} />
            <Route path="forms/lung-screening" element={<FormRoute Component={LungScreeningForm} />} />
            <Route path="forms/renal" element={<FormRoute Component={RenalForm} />} />
            <Route path="forms/abdomen" element={<FormRoute Component={AbdomenForm} />} />
            <Route path="forms/scrotal" element={<FormRoute Component={ScrotaForm} />} />
            <Route path="forms/thyroid" element={<FormRoute Component={ThyroidForm} />} />
            <Route path="forms/pelvic" element={<FormRoute Component={PelvicForm} />} />
            <Route path="forms/follicle-count" element={<FormRoute Component={FollicleCountForm} />} />
            <Route path="forms/soft-tissue" element={<FormRoute Component={SoftTissueForm} />} />
            <Route path="forms/aorta" element={<FormRoute Component={AortaForm} />} />
            <Route path="forms/carotid" element={<FormRoute Component={CarotidForm} />} />
            <Route path="forms/renal-artery-doppler" element={<FormRoute Component={RenalArteryDopplerForm} />} />
            <Route path="forms/lower-extremity-arterial" element={<FormRoute Component={LegArterialForm} />} />
            <Route path="forms/upper-extremity-arterial" element={<FormRoute Component={UpperExtremityArterialForm} />} />
            <Route path="forms/dialysis-access" element={<FormRoute Component={DialysisAccessForm} />} />
            <Route path="forms/dvt" element={<FormRoute Component={DVTForm} />} />
            <Route path="forms/upper-extremity-dvt" element={<FormRoute Component={UpperExtremityDVTForm} />} />
            <Route path="forms/pediatric-abdomen" element={<FormRoute Component={PediatricAbdomenForm} />} />
            <Route path="forms/pediatric-brain-spine" element={<FormRoute Component={PediatricBrainSpineForm} />} />
            <Route path="forms/pediatric-hips" element={<FormRoute Component={PediatricHipsForm} />} />
            <Route path="forms/first-trimester-ob" element={<FormRoute Component={FirstTrimesterOBForm} />} />
            <Route path="forms/second-trimester-ob" element={<FormRoute Component={SecondTrimesterMultipleOBForm} />} />
            <Route path="forms/bpp" element={<FormRoute Component={BPPForm} />} />
          </Route>
        </Routes>
      </Router>
    </div>
  );
}

export default App;
